import { Link, useLocation } from 'react-router-dom';
import {
  Ship, Cog, Package, MapPin, List, Plus, MessageCircle, DollarSign,
  BarChart3, Shield, Users, Database, Activity, BookOpen, Star,
  LogOut, Building, User, X, Home as HomeIcon,
} from 'lucide-react';
import { useAuth, useIsAuthenticated } from '../../stores/auth';
import { useDeviceMode } from '../../hooks/useDeviceMode';
import { useHaptics } from '../../hooks/useHaptics';
import { fixImageUrl } from '../../utils/images';
import GlobalSearch from '../GlobalSearch';
import { useEffect, useRef } from 'react';

interface Item {
  to: string;
  label: string;
  icon: React.ElementType;
  accent: string;
}

interface Section {
  title: string;
  items: Item[];
}

const ACCENTS = {
  cyan: 'rgba(34,211,238,0.16)',
  violet: 'rgba(167,139,250,0.16)',
  pink: 'rgba(244,114,182,0.15)',
  green: 'rgba(52,211,153,0.16)',
  amber: 'rgba(251,191,36,0.16)',
  red: 'rgba(251,113,133,0.16)',
  blue: 'rgba(96,165,250,0.16)',
} as const;

const ICON_COLOR = {
  cyan: '#22d3ee',
  violet: '#a78bfa',
  pink: '#f472b6',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb7185',
  blue: '#60a5fa',
} as const;

interface PwaMoreSheetProps {
  open: boolean;
  onClose: () => void;
}

export default function PwaMoreSheet({ open, onClose }: PwaMoreSheetProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const { isPWA, isMobileWeb } = useDeviceMode();
  const { impactLight } = useHaptics();
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const didScroll = useRef(false);

  useEffect(() => {
    if (!open) return;

    const sheet = sheetRef.current;
    if (!sheet) return;

    const focusable = sheet.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    sheet.addEventListener('keydown', handleKeyDown as any);
    sheet.setAttribute('tabindex', '-1');
    sheet.focus();

    return () => {
      sheet.removeEventListener('keydown', handleKeyDown as any);
      sheet.removeAttribute('tabindex');
    };
  }, [open, onClose]);

  if (!open) return null;

  const isAdmin = user?.is_admin === true;
  const isDealer = isAuthenticated && !isAdmin;
  const isPublic = !isAuthenticated;
  const mapPath = (isPWA || isMobileWeb) ? '/map-explorer-pwa' : '/map-explorer-desktop';

  const nav: Item[] = [
    { to: '/boats', label: 'Barche', icon: Ship, accent: ACCENTS.cyan },
    { to: '/motors', label: 'Motori', icon: Cog, accent: ACCENTS.amber },
    { to: '/accessories', label: 'Accessori', icon: Package, accent: ACCENTS.violet },
    { to: mapPath, label: 'Mappa Esplora', icon: MapPin, accent: ACCENTS.green },
    { to: '/general-map', label: 'Mappa Generale', icon: MapPin, accent: ACCENTS.blue },
  ];

  const gestione: Item[] = isPublic ? [] : [
    { to: '/my-boats', label: 'Le Mie Barche', icon: List, accent: ACCENTS.green },
    { to: '/my-motors', label: 'I Miei Motori', icon: Cog, accent: ACCENTS.green },
    { to: '/my-accessories', label: 'I Miei Accessori', icon: Package, accent: ACCENTS.violet },
    { to: '/add-boat', label: 'Aggiungi Barca', icon: Plus, accent: ACCENTS.pink },
    { to: '/add-motor', label: 'Aggiungi Motore', icon: Plus, accent: ACCENTS.pink },
    { to: '/add-accessory', label: 'Aggiungi Accessorio', icon: Plus, accent: ACCENTS.pink },
  ];

  const comunicazione: Item[] = isPublic ? [] : [
    { to: isAdmin ? '/admin?tab=whatsapp' : '/dashboard?tab=whatsapp', label: 'WhatsApp', icon: MessageCircle, accent: ACCENTS.green },
    { to: '/trades', label: 'Trattative', icon: DollarSign, accent: ACCENTS.amber },
  ];

  const dashboard: Item[] = isDealer ? [
    { to: '/dashboard', label: 'Dashboard Dealer', icon: BarChart3, accent: ACCENTS.cyan },
  ] : [];

  const admin: Item[] = isAdmin ? [
    { to: '/admin', label: 'Dashboard Admin', icon: BarChart3, accent: ACCENTS.red },
    { to: '/dealers', label: 'Commercianti', icon: Users, accent: ACCENTS.blue },
    { to: '/import-data', label: 'Import Dati', icon: Database, accent: ACCENTS.violet },
    { to: '/tracking-stats', label: 'Statistiche', icon: Activity, accent: ACCENTS.amber },
  ] : [];

  const network: Item[] = isPublic ? [] : [
    { to: '/dealers', label: 'Commercianti', icon: Users, accent: ACCENTS.blue },
  ];

  const info: Item[] = [
    { to: '/manuale', label: 'Guida', icon: BookOpen, accent: ACCENTS.cyan },
    { to: '/portfolio', label: 'Portfolio', icon: Star, accent: ACCENTS.amber },
  ];

  const home: Item[] = isPublic ? [
    { to: '/', label: 'Home', icon: HomeIcon, accent: ACCENTS.cyan },
  ] : [];

  const sections: Section[] = [
    ...(home.length ? [{ title: 'Home', items: home }] : []),
    { title: 'Navigazione', items: nav },
    ...(gestione.length ? [{ title: 'Gestione', items: gestione }] : []),
    ...(comunicazione.length ? [{ title: 'Comunicazione', items: comunicazione }] : []),
    ...(dashboard.length ? [{ title: 'Dashboard', items: dashboard }] : []),
    ...(network.length ? [{ title: 'Network', items: network }] : []),
    ...(admin.length ? [{ title: 'Admin', items: admin }] : []),
    { title: 'Info', items: info },
  ];

  const isActive = (path: string) => {
    const clean = path.split('?')[0];
    if (clean === '/') return location.pathname === '/';
    return location.pathname.startsWith(clean);
  };

  const handleLogout = async () => {
    impactLight();
    await logout();
    onClose();
  };

  return (
    <>
      <div className="au-sheet-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        className="au-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Menu completo"
        onTouchStart={(e) => {
          startY.current = e.touches[0].clientY;
          currentY.current = e.touches[0].clientY;
          didScroll.current = false;
        }}
        onTouchMove={(e) => {
          currentY.current = e.touches[0].clientY;
          if (sheetRef.current && sheetRef.current.scrollTop > 0) {
            didScroll.current = true;
          }
        }}
        onTouchEnd={() => {
          const diff = startY.current - currentY.current;
          if (diff < -60 && !didScroll.current) {
            onClose();
          }
        }}
      >
        <button className="au-sheet-close" onClick={() => { impactLight(); onClose(); }} aria-label="Chiudi menu" type="button">
          <X className="w-5 h-5" aria-hidden="true" />
        </button>

        <div className="au-sheet-body">
          {/* User card (speculare alla sidebar desktop) */}
          <div className="au-user-card">
            <div className="au-user-avatar">
              {user?.logo ? (
                <img src={fixImageUrl(user.logo)} alt={user?.company_name || 'Logo'} className="w-full h-full object-contain" width={52} height={52} />
              ) : isAdmin ? (
                <Shield className="w-6 h-6" />
              ) : isPublic ? (
                <User className="w-6 h-6" />
              ) : (
                <Building className="w-6 h-6" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="au-user-name">
                {isPublic ? 'Ospite' : isAdmin ? 'Admin' : (user?.company_name || 'Utente')}
              </p>
              <p className="au-user-sub">
                {isPublic ? 'Accedi per continuare' : (user?.email || 'Bentornato')}
              </p>
            </div>
          </div>

          {/* Global search */}
          <div className="mb-5">
            <GlobalSearch
              compact
              placeholder="Cerca nel catalogo..."
              onResultClick={onClose}
            />
          </div>

          {/* Sezioni speculari alla sidebar desktop */}
          {sections.map((section) => (
            <div key={section.title} className="au-section">
              <div className="au-section-title">
                {section.title}
              </div>
              <div className="au-section-grid">
                {section.items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => { impactLight(); onClose(); }}
                    className={`au-section-item ${isActive(item.to) ? 'is-active' : ''}`}
                  >
                    <div className="au-sec-icon" style={{ background: item.accent }}>
                      <item.icon className="w-[18px] h-[18px]" style={{ color: ICON_COLOR[item.accent as keyof typeof ICON_COLOR] || '#fff' }} />
                    </div>
                    <span className="au-sec-label">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="au-sheet-footer">
          {isPublic ? (
            <Link
              to="/login"
              onClick={() => { impactLight(); onClose(); }}
              className="au-cta au-cta-primary"
            >
              <User className="w-4 h-4" aria-hidden="true" />
              Accedi / Registrati
            </Link>
          ) : (
            <button onClick={handleLogout} className="au-cta au-cta-ghost" type="button">
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Esci
            </button>
          )}
        </div>
      </div>
    </>
  );
}
